/* Lite navbar: mismas funciones (toggle sidebar, título, acciones, notificaciones, menú de usuario) con UI mínima */
import { useAuthStore } from "@/stores/authStore";
import { useSidebarStore } from "@/stores/SidebarStore";
import { useTitleStore, useActionsStore } from "@/stores/titleStore";
import { Link, useNavigate } from "react-router";
import { useEffect, useRef, useState, useCallback } from "react";
import { signalRService } from "@/services/signalRService";
import { isSignalRAllowed, onSignalRAllowed } from "@/lib/signalRGate";
import { notifications, type NotificationDto } from "@/services/apiService";
import { PermissionElement } from "./permission-element";

export function NavbarLite() {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const title = useTitleStore((s) => s.title);
  const actions = useActionsStore((s) => s.actions);
  const navigate = useNavigate();

  const { isOpen, toggleSidebar } = useSidebarStore();

  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationList, setNotificationList] = useState<NotificationDto[]>(
    []
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [allNotifications, setAllNotifications] = useState<NotificationDto[]>(
    []
  );
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const isSlowNetwork = useRef<boolean>(
    (() => {
      try {
        // Network Information API (no tipada en TS por defecto)
        const anyNav = navigator as unknown as {
          connection?: { effectiveType?: string; saveData?: boolean };
        };
        const c = anyNav.connection;
        const slowTypes = ["slow-2g", "2g", "3g"];
        const saveData = !!(c && c.saveData === true);
        const eff = (c && c.effectiveType ? c.effectiveType : "").toLowerCase();
        return saveData || slowTypes.includes(eff);
      } catch {
        return false;
      }
    })()
  ) as React.MutableRefObject<boolean>;

  const loadNotifications = useCallback(async () => {
    setIsLoadingNotifications(true);
    try {
      const res = await notifications.getNotifications(true, 20);
      if (res.status?.success && res.data) setNotificationList(res.data);
    } catch {
      /* noop */
    } finally {
      setIsLoadingNotifications(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setIsLoadingAll(true);
    try {
      const res = await notifications.getNotifications(false, 1000);
      if (res.status?.success && res.data) setAllNotifications(res.data);
    } catch {
      /* noop */
    } finally {
      setIsLoadingAll(false);
    }
  }, []);

  const loadUnread = useCallback(async () => {
    try {
      const res = await notifications.getUnreadCount();
      if (res.status?.success && res.data) setUnreadCount(res.data.unreadCount);
    } catch {
      /* noop */
    }
  }, []);

  type HubEventPayload = Record<string, unknown> & {
    id?: number | string;
    title?: string;
    message?: string;
    icon?: string;
    bgColor?: string;
    iconColor?: string;
    order?: number;
    priority?: number;
    isPush?: boolean;
    href?: string;
    time?: string;
    tipoEnvio?: string;
  };

  const handleHubEvent = useCallback(
    (event: string, data: unknown) => {
      switch (event) {
        case "new": {
          const d = (data as HubEventPayload) || {};
          const rawId = d.id as number | string | undefined;
          const idNum =
            typeof rawId === "number"
              ? rawId
              : rawId
              ? Number.parseInt(String(rawId), 10) || 0
              : 0;
          const n: NotificationDto = {
            idNotificacion: idNum,
            titulo: (d.title as string) ?? "",
            mensaje: (d.message as string) ?? "",
            icono: (d.icon as string) ?? "",
            colorFondo: (d.bgColor as string) ?? "",
            colorIcono: (d.iconColor as string) ?? "",
            orden: (d.order as number) ?? 0,
            prioridad: (d.priority as number) ?? 0,
            esPush: (d.isPush as boolean) ?? false,
            href: (d.href as string) ?? undefined,
            leido: false,
            fecha: new Date(),
            tiempoRelativo: (d.time as string) ?? "Ahora",
            tipoEnvio: (d.tipoEnvio as string) ?? "Usuario",
          };
          setNotificationList((prev) =>
            prev.some((x) => x.idNotificacion === n.idNotificacion)
              ? prev
              : [n, ...prev]
          );
          setUnreadCount((c) => c + 1);
          break;
        }
        case "read":
          setNotificationList((prev) =>
            prev.map((n) =>
              n.idNotificacion === (data as number | string)
                ? { ...n, leido: true }
                : n
            )
          );
          setUnreadCount((c) => Math.max(0, c - 1));
          break;
        case "allRead":
          setNotificationList((prev) =>
            prev.map((n) => ({ ...n, leido: true }))
          );
          setUnreadCount(0);
          break;
        case "unreadCount":
          if (typeof data === "number") setUnreadCount(data);
          break;
        case "connected":
          loadNotifications();
          loadUnread();
          break;
      }
    },
    [loadNotifications, loadUnread]
  );

  useEffect(() => {
    if (!(token && user)) return;
    let unsubscribe: (() => void) | null = null;
    const start = () => {
      signalRService.connect(token);
      unsubscribe = signalRService.subscribe(handleHubEvent);
      loadNotifications();
      loadUnread();
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    };
    if (isSlowNetwork.current) return; // also skip on slow networks until user interacts
    if (isSignalRAllowed()) start();
    else onSignalRAllowed(start);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [token, user, handleHubEvent, loadNotifications, loadUnread]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node))
        setNotifOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node))
        setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const logout = async () => {
    signalRService.isIntentionalDisconnect = false;
    await signalRService.disconnect(true);
    useAuthStore.getState().logout();
    navigate("/login");
  };

  return (
    <nav className="sticky top-0 z-50 bg-slate-50 border-b border-slate-300">
      <div className="px-3">
        <div className="h-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded border border-slate-300 text-slate-600 bg-slate-50 hover:bg-slate-100"
              aria-label="Abrir menú"
            >
              <svg
                className={`h-5 w-5 ${isOpen ? "rotate-90" : ""}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div className="font-semibold text-slate-700 text-sm">{title}</div>
          </div>

          <div className="flex items-center gap-1">
            {actions && <div className="mr-1">{actions}</div>}

            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen((v) => !v)}
                className="p-2 rounded border border-slate-300 text-slate-600 bg-slate-50 hover:bg-slate-100"
                aria-label="Notificaciones"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 mt-1 w-80 max-w-[90vw] bg-slate-50 border border-slate-300 rounded shadow z-50">
                  <div className="px-3 py-2 text-sm font-medium border-b border-slate-300 text-slate-700">
                    Notificaciones
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {isLoadingNotifications ? (
                      <div className="p-3 text-sm text-slate-500">Cargando…</div>
                    ) : notificationList.length ? (
                      notificationList.slice(0, 10).map((n) => (
                        <button
                          key={n.idNotificacion}
                          onClick={() => {
                            if (!n.leido)
                              notifications
                                .markAsRead(n.idNotificacion)
                                .catch(() => {});
                            if (n.href) navigate(n.href);
                            setNotifOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm border-b border-slate-200 last:border-0 text-slate-600 ${
                            n.leido ? "" : "bg-blue-100"
                          }`}
                        >
                          <div className="font-medium text-slate-700">
                            {n.titulo}
                          </div>
                          <div className="text-slate-600 text-xs">
                            {n.mensaje}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-3 text-sm text-slate-500">
                        No tienes notificaciones
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t border-slate-300 flex gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={() =>
                          notifications
                            .markAllAsRead()
                            .then(loadNotifications)
                            .then(loadUnread)
                        }
                        className="text-xs text-blue-600"
                      >
                        Marcar todas como leídas
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowAllNotifications(true);
                        loadAll();
                        setNotifOpen(false);
                      }}
                      className="ml-auto text-xs text-blue-600"
                    >
                      Ver todas
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={userRef}>
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2 p-2 border border-slate-300 rounded bg-slate-50 hover:bg-slate-100"
              >
                <img
                  className="w-6 h-6 rounded"
                  alt="Avatar"
                  src={`https://ui-avatars.com/api/?name=${user?.nombre_usuario}&background=0D8ABC&color=fff&rounded=true`}
                />
                <span className="hidden sm:block text-xs text-slate-600">
                  @{user?.nombre_usuario}
                </span>
                <svg
                  className="w-3 h-3 text-slate-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-1 w-56 bg-slate-50 border border-slate-300 rounded shadow z-50">
                  <div className="px-3 py-2 border-b border-slate-300">
                    <div className="text-sm font-medium text-slate-700">
                      {user?.nombre}
                    </div>
                    <div className="text-xs text-slate-500">
                      @{user?.nombre_usuario}
                    </div>
                  </div>
                  <div className="py-1 text-sm">
                    <a href="#" className="block px-3 py-2 hover:bg-slate-100 text-slate-600">
                      Mi Perfil
                    </a>
                    <PermissionElement
                      requiredPermission="puede_ver_sistemas_panel_administracion"
                      children={
                        <>
                          <Link
                            to="/settings"
                            className="block px-3 py-2 hover:bg-slate-100 text-slate-600"
                          >
                            Configuración
                          </Link>
                          <Link
                            to="/settings/panel-administracion"
                            className="block px-3 py-2 hover:bg-slate-100 text-slate-600"
                          >
                            Panel de Admin
                          </Link>
                        </>
                      }
                    />
                  </div>
                  <div className="border-t border-slate-300">
                    <button
                      onClick={logout}
                      className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 text-sm"
                    >
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAllNotifications && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowAllNotifications(false)}
          />
          <div className="relative bg-slate-50 rounded shadow w-full max-w-2xl mx-4 max-h-[80vh] overflow-auto">
            <div className="p-3 border-b border-slate-300 flex items-center justify-between">
              <div className="font-semibold text-slate-700">Todas las notificaciones</div>
              <button
                className="text-sm"
                onClick={() => setShowAllNotifications(false)}
              >
                Cerrar
              </button>
            </div>
            <div>
              {isLoadingAll ? (
                <div className="p-4 text-sm text-slate-500">Cargando…</div>
              ) : allNotifications.length ? (
                allNotifications.map((n) => (
                  <button
                    key={n.idNotificacion}
                    onClick={() => {
                      if (!n.leido)
                        notifications
                          .markAsRead(n.idNotificacion)
                          .catch(() => {});
                      if (n.href) navigate(n.href);
                      setShowAllNotifications(false);
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-slate-200 last:border-0 ${
                      n.leido ? "" : "bg-blue-100"
                    }`}
                  >
                    <div className="font-medium text-slate-700">{n.titulo}</div>
                    <div className="text-sm text-slate-600">{n.mensaje}</div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-sm text-slate-500">
                  No hay notificaciones
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
